import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { inked, toon } from "../toon";
import { facets, smooth } from "./geom";
import { HEAD_Y } from "./look";

// Long hair (#227, Maddy's), head space, face +z: the part that stays on the
// head (a scalp of hair, a side-swept fringe and locks over the ears down to
// the jaw) and the long fall down the back, a curtain in its own group,
// `hair-swing`, pivoting on the head's centre. `HairSwing` (`hair-swing.ts`)
// turns that group every frame so the fall hangs, swings and trails behind
// the rider, kept out over the back and the backpack. The toy's pieces are
// here; the natural face's are `head-natural.ts`'s, on the same curtain.

/** Where the fall ends, in torso space: below the shoulders (0.415), over the backpack's top. */
export const LONG_HAIR_END = 0.3;
/** The name of the group `HairSwing` turns. */
export const HAIR_SWING = "hair-swing";

/** The fall's shape: the sphere it starts on (head space) and how far below the head's centre it reaches. */
export interface CurtainShape {
  /** The radius it lies on over the back of the skull. */
  radius: number;
  /** How far below the head's centre the ends hang (head space). */
  reach: number;
  /** How much further out the fall stands below the head (head space): a smaller head's fall clears the same body. */
  out: number;
  /** How many segments round and down (before `facets`). */
  around: number;
  down: number;
}

/** The toy's fall's radius: a smaller head's stands out to the same place on the body. */
const TOY_RADIUS = 0.214;

/**
 * The fall's shape on a head `size` times the toy's lying on `radius`:
 * ending at `LONG_HAIR_END` on the torso, standing as far out from the
 * body as the toy's.
 */
export function curtainFor(size: number, radius: number, around: number, down: number): CurtainShape {
  const reach = (HEAD_Y - 0.16 * (1 - size) - LONG_HAIR_END) / size;
  return { radius, reach, out: Math.max(0, TOY_RADIUS - radius * size) / size, around, down };
}

/** Half the angle the fall wraps round the back of the head, from its top (`v` 0) to its ends (`v` 1). */
const WRAP_TOP = 1.3;
const WRAP_END = 0.78;
/** How high up the back of the skull the fall starts (radians above level). */
const TOP_ELEVATION = 1.05;
/** How far the fall stands out from the back of the neck by the time it reaches its ends, per metre down. */
const FLARE = 0.45;
const THICK = 0.022;

/**
 * A point on the fall at `u` across (0 to 1, one side round the back to
 * the other) and `v` down (0 at its top, 1 at its ends), `inset` in from
 * its outer surface. Over the skull it lies on a sphere; below the head's
 * centre it falls straight down, standing further out from the neck the
 * lower it goes, so it lies out over the shoulders and the backpack.
 */
export function curtainPoint(shape: CurtainShape, u: number, v: number, inset = 0, out = new THREE.Vector3()): THREE.Vector3 {
  const { radius, reach, out: standOff } = shape;
  const arc = radius * TOP_ELEVATION;
  const total = arc + reach;
  const s = v * total;
  const wrap = WRAP_TOP + (WRAP_END - WRAP_TOP) * smooth(v);
  const a = (u * 2 - 1) * wrap;
  let r: number;
  let y: number;
  if (s < arc) {
    const e = TOP_ELEVATION * (1 - s / arc);
    r = radius * Math.cos(e) - inset;
    y = (radius - inset) * Math.sin(e);
  } else {
    const drop = s - arc;
    r = radius + standOff * smooth(Math.min(1, drop / 0.12)) + FLARE * drop - inset;
    y = -drop;
  }
  // Round from the back (-z) toward +x; the ends taper in a little, like hair gathering.
  const taper = 1 - 0.12 * smooth(Math.max(0, (v - 0.6) / 0.4));
  return out.set(r * Math.sin(a) * taper, y, -r * Math.cos(a));
}

/** A grid of quads over (u, v) in [0, 1]², with uvs, smooth normals and its winding flipped if `flip`. */
function gridGeometry(nu: number, nv: number, at: (u: number, v: number) => THREE.Vector3, flip: boolean): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  for (let j = 0; j <= nv; j++) {
    for (let i = 0; i <= nu; i++) {
      const p = at(i / nu, j / nv);
      pos.push(p.x, p.y, p.z);
      uv.push(i / nu, 1 - j / nv);
    }
  }
  const index: number[] = [];
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      const a = j * (nu + 1) + i;
      const b = a + nu + 1;
      if (flip) index.push(a, a + 1, b, b, a + 1, b + 1);
      else index.push(a, b, a + 1, b, b + 1, a + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}

/**
 * The fall as a thin closed slab: its outer surface, its inner one, and
 * the strips along its sides and ends that join them, one mesh.
 */
export function curtainGeometry(shape: CurtainShape): THREE.BufferGeometry {
  const nu = facets(shape.around);
  const nv = facets(shape.down);
  const outer = gridGeometry(nu, nv, (u, v) => curtainPoint(shape, u, v), true);
  const inner = gridGeometry(nu, nv, (u, v) => curtainPoint(shape, u, v, THICK), false);
  // The rims: down each side, and across the ends.
  const left = gridGeometry(1, nv, (w, v) => curtainPoint(shape, 0, v, w * THICK), false);
  const right = gridGeometry(1, nv, (w, v) => curtainPoint(shape, 1, v, w * THICK), true);
  const ends = gridGeometry(nu, 1, (u, w) => curtainPoint(shape, u, 1, w * THICK), false);
  const parts = [outer, inner, left, right, ends];
  const merged = mergeGeometries(parts)!;
  for (const g of parts) g.dispose();
  return merged;
}

/** The fall in its own group (`hair-swing`), pivoting on the head's centre. */
export function buildCurtain(shape: CurtainShape, mat: THREE.Material, extra: THREE.Object3D[] = []): THREE.Group {
  const swing = new THREE.Group();
  swing.name = HAIR_SWING;
  const fall = inked(curtainGeometry(shape), mat, 0.01);
  fall.name = "hair-fall";
  swing.add(fall, ...extra);
  return swing;
}

/**
 * The toy's long hair: a cap of hair over the skull, a side-swept fringe,
 * locks down over the ears to the jaw, and the fall down the back.
 */
export function buildToyLongHair(color: string, size: number): THREE.Group {
  const hair = new THREE.Group();
  hair.name = "long-hair";
  const mat = toon(color, { roughness: 0.6 }, "hair");
  // The cap: tipped back so it sits high on the forehead and low at the nape.
  const cap = inked(new THREE.SphereGeometry(0.212, facets(24), facets(10, 2), 0, Math.PI * 2, 0, Math.PI * 0.5), mat, 0.015);
  cap.name = "hair";
  cap.scale.set(1.03, 1.05, 1.03);
  cap.rotation.x = -0.32;
  hair.add(cap);
  // The fringe: a soft roll swept from a parting over one eye to the far temple.
  const fringe = inked(new THREE.CapsuleGeometry(0.04, 0.2, facets(4, 1), facets(10)), mat, 0.01);
  fringe.name = "fringe";
  fringe.rotation.set(0.15, 0, Math.PI / 2 - 0.3);
  fringe.scale.set(1, 1, 0.6);
  fringe.position.set(0.01, 0.15, 0.15);
  hair.add(fringe);
  // Locks either side of the face, over the ears, down to the jaw.
  for (const side of [-1, 1]) {
    const geo = new THREE.CapsuleGeometry(0.045, 0.2, facets(4, 1), facets(10));
    const lock = inked(geo, mat, 0.01);
    lock.name = "lock";
    lock.scale.set(0.7, 1, 1.4);
    lock.rotation.z = side * 0.12;
    lock.position.set(side * 0.2, -0.04, -0.03);
    hair.add(lock);
  }
  hair.add(buildCurtain(curtainFor(size, TOY_RADIUS, 18, 12), mat));
  return hair;
}
